import React, { useState, useEffect, useRef } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ChatMessageBubble } from "./ChatMessageBubble";
import { Message } from "@/types/inbox.types";

interface ChatMessagesProps {
  messages: Message[];
  isLoading: boolean;
  agentName?: string;
  agentAvatar?: string;
  brandColor?: string;
}

export const ChatMessages = ({
  messages,
  isLoading,
  agentName,
  agentAvatar,
  brandColor,
}: ChatMessagesProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // Track manual scroll position
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    const onScroll = () => {
      const atBottom =
        Math.abs(
          container.scrollHeight - container.scrollTop - container.clientHeight,
        ) < 50;
      setAutoScroll(atBottom);
    };

    container.addEventListener("scroll", onScroll);
    return () => container.removeEventListener("scroll", onScroll);
  }, []);

  // Actual scroll - only scroll the messages container, not the window
  const scrollToBottom = () => {
    const container = chatContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  };

  useEffect(() => {
    if (autoScroll && chatContainerRef.current && messages.length > 0) {
      // Use a short timeout to ensure the DOM has updated
      setTimeout(scrollToBottom, 100);
    }
  }, [messages, isLoading, autoScroll]);

  return (
    <div className="h-full w-full absolute inset-0">
      {/* Scroll container */}
      <div
        ref={chatContainerRef}
        className="h-full w-full overflow-y-auto p-4 space-y-4"
        style={{ scrollBehavior: "smooth" }}
      >
        {/* Message content */}
        {messages.length === 0 && !isLoading ? (
          <div className="flex items-center justify-center py-10">
            <p className="text-muted-foreground">
              No messages in this conversation yet.
            </p>
          </div>
        ) : (
          messages.map((msg) => (
            <ChatMessageBubble
              key={msg.id}
              content={msg.content}
              senderType={msg.sender_type}
              timestamp={msg.created_at}
              agentName={agentName}
              agentAvatar={agentAvatar}
              brandColor={brandColor}
            />
          ))
        )}

        {/* Typing indicator */}
        {isLoading && (
          <div className="flex items-start">
            <div className="flex items-center p-3 rounded-[7px] max-w-[80%] bg-[#f2f2f2]">
              <div className="typing-animation flex space-x-1">
                <div
                  className="dot w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0ms" }}
                />
                <div
                  className="dot w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: "150ms" }}
                />
                <div
                  className="dot w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: "300ms" }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Anchor for scroll-to-bottom */}
        <div ref={messagesEndRef} className="h-1" />
      </div>

      {/* Floating "scroll to latest" button */}
      {!autoScroll && messages.length > 0 && (
        <Button
          onClick={() => {
            scrollToBottom();
            setAutoScroll(true);
          }}
          className="absolute bottom-4 right-4 h-10 w-10 p-2 rounded-full shadow-md z-20"
          style={{ backgroundColor: brandColor || "#060520", color: "#FFFFFF" }}
          aria-label="Scroll to latest messages"
        >
          <ChevronDown size={20} />
        </Button>
      )}
    </div>
  );
};
